<template>
  <b-row class="align-items-center">
    <LanguagePopover
      v-if="setting('ui_menu_language')"
      class="position-absolute"
      style="right: 15px; top: 15px"
    />

    <b-col md="6" offset-xl="2" order-md="2" class="col-12 mb-5 mb-md-0">
      <div class="text-center">
        <img
          :src="
            setting(
              'ui_login_image',
              '@/Assets/images/illustrations/happiness.svg'
            )
          "
          class="img-fluid"
        />
      </div>
    </b-col>

    <b-col md="5" xl="4" order-md="1" class="my-5">
      <template v-if="!tokenNeeded">
        <h1 class="display-4 text-center mb-3">
          {{ __('login.title') }}
        </h1>

        <p class="text-muted text-center mb-5">
          {{ __('login.subtitle', { name: systemName }) }}
        </p>

        <form @submit.prevent="login">
          <TextField
            v-model="email"
            name="email"
            type="email"
            :label="__('login.email-label')"
            :placeholder="__('login.email-placeholder')"
          />

          <TextField
            v-model="password"
            type="password"
            name="password"
            :label="__('login.password-label')"
          >
            <template slot="labelRight">
              <RouterLink
                :to="{ name: 'auth.password-recovery' }"
                class="form-text small text-muted"
                tabindex="-1"
              >
                {{ __('login.forgot-password-link') }}
              </RouterLink>
            </template>
          </TextField>

          <b-button
            block
            variant="primary"
            label=""
            class="btn-lg mb-3"
            type="submit"
          >
            {{ __('login.sign-in-button') }}
          </b-button>
        </form>
      </template>

      <template v-else>
        <h1 class="display-4 text-center mb-3">
          {{ __('login.verify') }}
        </h1>

        <p class="text-muted text-center mb-5">
          {{ __('login.tfa') }}
        </p>

        <form @submit.prevent="loginWithTfa">
          <TextField
            v-model="token"
            name="token"
            type="text"
            :label="__('login.confirmation-code')"
            :placeholder="__('password.token-placeholder')"
          />

          <b-button
            block
            variant="primary"
            label=""
            class="btn-lg mb-3"
            type="submit"
          >
            {{ __('buttons.confirm') }}
          </b-button>
        </form>
      </template>
    </b-col>
  </b-row>
</template>

<script>
import { GET_AUTH_USER_ACTION } from '@/Modules/Auth/AuthModule.js'
import { SHOW_INFORMATION_TOAST_ACTION } from '@/Modules/Base/BaseModule.js'
import { AUTH_USER_GETTER, RESET_AUTH_USER } from '@/Modules/Auth/AuthModule.js'
import {
  GET_GUEST_SETTINGS,
  GET_SETTINGS_ACTION,
} from '@/Modules/Base/SettingModule.js'
import store from '@/Setup/SetupStore.js'
import { mapMutations } from 'vuex'
import { LS_ROUTE_KEY } from '@/Setup/Routes/SetupRouter.js'
import LanguagePopover from '@/Modules/Base/Components/LanguagePopover.vue'
import { fallbackLocale, localeKey } from '@/Setup/SetupI18n.js'
import ProfileService from '@/Modules/Profile/ProfileService.js'
import * as Sentry from '@sentry/vue'
import AuthService from '@/Modules/Auth/AuthService'

export default {
  name: 'Login',

  components: { LanguagePopover },

  inject: ['$validator'],

  data() {
    return {
      email: null,
      password: null,
      token: null,
      tokenNeeded: null,
    }
  },

  computed: {
    systemName() {
      return this.setting('ui_system_name') || 'Quote & Bind'
    },
  },

  async created() {
    await store.dispatch('SettingModule/' + GET_GUEST_SETTINGS)

    if (localStorage.getItem('expired_session')) {
      localStorage.removeItem('expired_session')
      await store.dispatch(`BaseModule/${SHOW_INFORMATION_TOAST_ACTION}`, {
        title: this.__('session.expired-title'),
        text: this.__('session.expired-text'),
        variant: 'warning',
      })
    }
    if (
      !localStorage.getItem('auth_token') &&
      !!store.getters['AuthModule/' + AUTH_USER_GETTER]
    ) {
      this.logout()
    }
  },

  methods: {
    login() {
      this.axios
        .post('/login', {
          email: this.email,
          password: this.password,
        })
        .then(async (response) => {
          if (response.data) {
            await localStorage.setItem('auth_token', response.data.access_token)
            await store.dispatch('AuthModule/' + GET_AUTH_USER_ACTION)
            await store.dispatch('SettingModule/' + GET_SETTINGS_ACTION)
            await AuthService.redirectSpecialRoleUser()
            this.syncLocale()
            this.redirectToApp()
          }
        })
        .catch((error) => {
          // TFA needed
          if (error.response.status === 401) {
            this.tokenNeeded = true

            return
          }

          this.setErrorsFromResponse(error)
        })
    },

    loginWithTfa() {
      this.axios
        .post('/login', {
          email: this.email,
          password: this.password,
          token: this.token,
        })
        .catch(() => {
          this.errors.add({ field: 'token', msg: this.__('login.tfa-error') })
        })
        .then(async (response) => {
          if (response.data) {
            await localStorage.setItem('auth_token', response.data.access_token)
            await store.dispatch('AuthModule/' + GET_AUTH_USER_ACTION)
            await store.dispatch('SettingModule/' + GET_SETTINGS_ACTION)
            this.$navigate('home')
          }
        })
    },

    ...mapMutations('AuthModule', {
      resetAuthUser: RESET_AUTH_USER,
    }),

    logout() {
      localStorage.removeItem('auth_token')
      this.resetAuthUser()
      this.$navigate('auth.login')
    },

    redirectToApp() {
      if (!this.$router) {
        return
      }
      let routeName = 'home'
      let lastRoutePath

      try {
        lastRoutePath = JSON.parse(sessionStorage.getItem(LS_ROUTE_KEY))
      } catch (error) {
        console.error(error)
      }

      if (
        this.authUser().roles.find((role) => role.name === 'Broker') ||
        !this.authUser().isCoverholder()
      ) {
        routeName = 'quotes.quick-select-schema'
      }

      try {
        if (lastRoutePath) {
          this.$router.push({ path: lastRoutePath }).catch(() => {})
        }
      } catch (err) {
        Sentry.captureException(err)
      } finally {
        this.$navigate(routeName)
      }
    },

    syncLocale() {
      if (localStorage.getItem(localeKey) !== this.authUser().locale) {
        ProfileService.changeLocale(
          localStorage.getItem(localeKey) || fallbackLocale
        )
      }
    },
  },
}
</script>
